// src/App.js
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { UpgradeProvider } from "./components/gating/UpgradeContext";
import ProtectedRoute from "./components/ProtectedRoute";

import Home from "./pages/Home";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import GoogleCallback from "./pages/GoogleCallback";
import Contact from "./pages/Contact";

// Portfolio Section
import PortfolioAnalysis from "./pages/PortfolioAnalysis";
import Overview from "./pages/portfolio/Overview";
import Performance from "./pages/portfolio/Performance";
import Holdings from "./pages/portfolio/Holdings";
import InsightsPage from "./pages/insights/Insights";
import TaxHarvest from "./pages/portfolio/TaxHarvest";
import Recommendations from "./pages/portfolio/Recommendations";

// User Pages
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import Welcome from "./pages/Welcome";
import PublicPortfolio from "./pages/PublicPortfolio";
import ConversationHistory from "./pages/ConversationHistory";

// Blogs
import Blogs from "./pages/Blogs";
import BlogDetail from "./pages/BlogDetail";

// Community
import Community from "./pages/community/Community";
import UserProfile from "./pages/community/UserProfile";

// Advisor marketplace (Phase 2 — authoring surface)
import AdvisorShell from "./pages/advisor/AdvisorShell";
import AdvisorDashboard from "./pages/advisor/AdvisorDashboard";
import AdvisorProfileEditor from "./pages/advisor/AdvisorProfileEditor";
import SkillList from "./pages/advisor/SkillList";
import SkillEditor from "./pages/advisor/SkillEditor";
import PlanList from "./pages/advisor/PlanList";
import PlanEditor from "./pages/advisor/PlanEditor";

// Marketplace (Phase 3 — investor-facing discovery)
import Marketplace from "./pages/marketplace/Marketplace";
import AdvisorPublicProfile from "./pages/marketplace/AdvisorPublicProfile";

// Pricing (platform subscription tiers)
import Pricing from "./pages/Pricing";
import ChoosePlan from "./pages/ChoosePlan";

// Admin dashboard (staff-only)
import AdminShell from "./pages/admin/AdminShell";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSupportTickets from "./pages/admin/AdminSupportTickets";
import AdminAdvisors from "./pages/admin/AdminAdvisors";
import AdminPlans from "./pages/admin/AdminPlans";
import ResearchOverview from "./pages/admin/ResearchOverview";
import ResearchAgentDetail from "./pages/admin/ResearchAgentDetail";
import ResearchApprovals from "./pages/admin/ResearchApprovals";

// Chat Assistant Widget
import ChatWidget from "./components/ChatWidget";
import OnboardingBanner from "./components/OnboardingBanner";

function App() {
  return (
    <Router>
      <AuthProvider>
        <NotificationProvider>
         <UpgradeProvider>
          {/* 🟣 AI Assistant Chat Available on All Pages */}
          <ChatWidget />
          <OnboardingBanner />

        <Routes>
          {/* Public Pages */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path="/contact" element={<Contact />} />

          {/* Onboarding wizard — protected, but allowed for unboarded users */}
          <Route
            path="/welcome"
            element={
              <ProtectedRoute>
                <Welcome />
              </ProtectedRoute>
            }
          />

          {/* Public Blogs */}
          <Route path="/blogs" element={<Blogs />} />
          <Route path="/blogs/:slug" element={<BlogDetail />} />

          {/* Public marketplace — logged-out visitors can browse advisors.
              Subscribe button on the detail page routes through /login when
              the visitor isn't authed. */}
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/marketplace/advisor/:slug" element={<AdvisorPublicProfile />} />

          {/* Public pricing — tier selection requires auth (handled in-page) */}
          <Route path="/pricing" element={<Pricing />} />

          {/* Post-signup plan selection (self-guards for auth) */}
          <Route path="/choose-plan" element={<ChoosePlan />} />

          {/* Public shared portfolio (no auth) */}
          <Route path="/p/:slug" element={<PublicPortfolio />} />

          {/* Insights — top-level since it's the AI value-prop, no longer
              nested under PortfolioAnalysis. /insights/chats is a peer. */}
          <Route
            path="/insights"
            element={
              <ProtectedRoute>
                <InsightsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights/chats"
            element={
              <ProtectedRoute>
                <ConversationHistory />
              </ProtectedRoute>
            }
          />

          {/* Protected: Community */}
          <Route
            path="/community"
            element={
              <ProtectedRoute>
                <Community />
              </ProtectedRoute>
            }
          />
          <Route
            path="/community/profile/:userId"
            element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            }
          />

          {/* Protected: User Pages */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />

          {/* Protected: Portfolio Section */}
          <Route
            path="/portfolio"
            element={
              <ProtectedRoute>
                <PortfolioAnalysis />
              </ProtectedRoute>
            }
          >
            <Route index element={<Overview />} />
            <Route path="performance" element={<Performance />} />
            <Route path="holdings" element={<Holdings />} />
            <Route path="tax" element={<TaxHarvest />} />
            <Route path="recommendations" element={<Recommendations />} />
          </Route>

          {/* Protected: Advisor authoring (Phase 2). AdvisorShell guards
              non-advisors and redirects them to Settings → Subscription. */}
          <Route
            path="/advisor"
            element={
              <ProtectedRoute>
                <AdvisorShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdvisorDashboard />} />
            <Route path="profile" element={<AdvisorProfileEditor />} />
            <Route path="skills" element={<SkillList />} />
            <Route path="skills/new" element={<SkillEditor />} />
            <Route path="skills/:id" element={<SkillEditor />} />
            <Route path="plans" element={<PlanList />} />
            <Route path="plans/new" element={<PlanEditor />} />
            <Route path="plans/:id" element={<PlanEditor />} />
          </Route>

          {/* Protected: Admin dashboard. AdminShell guards non-staff users
              and redirects them home. */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="advisors" element={<AdminAdvisors />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="tickets" element={<AdminSupportTickets />} />
            <Route path="research" element={<ResearchOverview />} />
            <Route path="research/approvals" element={<ResearchApprovals />} />
            <Route path="research/:key" element={<ResearchAgentDetail />} />
          </Route>
        </Routes>
         </UpgradeProvider>
        </NotificationProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
